import axios from '@axios'
import jwtDefaultConfig from '@core/auth/jwt/jwtDefaultConfig'

// signIn guarda el mismo objeto en localStorage.userData y en currentUser, asi que al
// recargar se puede reconstruir de ahi. Sin esto el store arrancaba vacio en cada F5 y todo
// lo que decide por rol (el menu lateral, el layout) se quedaba sin usuario.
const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('userData')) || {}
  } catch (error) {
    return {}
  }
}

const state = () => ({
  currentUser: readStoredUser(),
  loading: false,
})

const getters = {
  currentUser: state => state.currentUser,
  loading: state => state.loading,
}

const mutations = {
  setCurrentUser: (state, payload) => {
    state.currentUser = payload
  },
  CHANGE_CURRENT_USER: (state, { field, value }) => {
    state.currentUser[field] = value
  },
  CHANGE_CURRENT_USER_COMPANY_LOGO: (state, logo) => {
    if (typeof state.currentUser.company_information === 'string') {
      state.currentUser.company_information = {}
    }
    state.currentUser.company_information.logo = logo
  },
  clearCurrentUser: state => {
    state.currentUser = {}
  },
  loading: (state, payload) => {
    state.loading = payload
  },
}

// El token tiene que quedar guardado ANTES de commitear al usuario: en cuanto currentUser
// cambia se monta la barra superior, su campana dispara una peticion en created(), y sin
// token esa peticion responde 401 y tumba la sesion que se acaba de crear.
const persistSession = data => {
  if (!data) return

  if (data.token) localStorage.setItem(jwtDefaultConfig.storageTokenKeyName, data.token)
  if (data.refresh_token) {
    localStorage.setItem(jwtDefaultConfig.storageRefreshTokenKeyName, data.refresh_token)
  }
  localStorage.setItem('userData', JSON.stringify(data))
}

const actions = {
  signIn(context, payload) {
    return new Promise((resolve, reject) => {
      context.commit('loading', true)
      axios
        .post('/v1/login', {
          auth: {
            email: payload.email.toLowerCase(),
            password: payload.password,
          },
        }, { skipErrorToast: true })
        .then(response => {
          persistSession(response.data)
          context.commit('setCurrentUser', response.data)
          resolve(response.data)
        })
        .catch(error => reject(error))
        .finally(() => {
          context.commit('loading', false)
        })
    })
  },
  signInWithGoogle(context, payload) {
    return new Promise((resolve, reject) => {
      context.commit('loading', true)
      axios
        .post('/v1/login/google', {
          auth: {
            id_token: payload.idToken,
          },
        }, { skipErrorToast: true })
        .then(response => resolve(response.data))
        .catch(error => reject(error))
        .finally(() => {
          context.commit('loading', false)
        })
    })
  },
  signOut: context => {
    context.commit('clearCurrentUser')
    this.$router.push('/')
  },
  requestPassword(context, { email, redirect = true }) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/request_password', {
          email,
        }, { skipErrorToast: true })
        .then(response => {
          resolve(response)
          if (redirect) {
            this.$router.push('/login')
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  },
  resetPassword(context, { token, user }) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/reset_password', {
          token,
          password: user.password,
          password_confirmation: user.password_confirmation,
        }, { skipErrorToast: true })
        .then(response => {
          if (response.data?.token) persistSession(response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response.data)
        })
    })
  },
  confirmEmail(_, token) {
    return axios.post('/v1/confirm_email', { token }, { skipErrorToast: true }).then(response => response.data)
  },
  confirmEmailChange(_, token) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/confirm_email_change', {
          token,
        }, { skipErrorToast: true })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response?.data || error)
        })
    })
  },
  isLoggedIn(context) {
    if (localStorage.currentUser) {
      context.commit('setCurrentUser', JSON.parse(localStorage.currentUser))
      this.$router.push('/dashboard')
    }
  },
  createUser(_, payload) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/users', {
          user: payload,
        })
        .then(() => {
          resolve()
        })
        .catch(error => {
          reject(error.response.data)
        })
    })
  },
  createCustomer(_, {
    acceptLegal,
    password,
    skipErrorToast,
    ...payload
  }) {
    return new Promise((resolve, reject) => {
      axios
        .post('/v1/customers', {
          customer: payload,
          accept_legal: acceptLegal,
          password,
        }, { skipErrorToast: Boolean(skipErrorToast) })
        .then(response => {
          if (response.data?.token) persistSession(response.data)
          resolve(response.data)
        })
        .catch(error => {
          reject(error.response.data)
        })
    })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
